import React from 'react';
import {
    Box,
    IconButton,
    Typography,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { ZoomIn, ZoomOut } from '@mui/icons-material';
import type { MetricType } from '../types';

interface ChartHeaderProps {
    metric: MetricType;
    onMetricChange: (event: React.MouseEvent<HTMLElement>, newMetric: MetricType | null) => void;
    isModal?: boolean;
    zoomControls?: {
        handleZoomIn: (cursorX?: number, useRecentDate?: boolean) => void;
        handleZoomOut: (cursorX?: number, useRecentDate?: boolean) => void;
        canZoomIn: boolean;
        canZoomOut: boolean;
    };
}

export const ChartHeader: React.FC<ChartHeaderProps> = ({
    metric,
    onMetricChange,
    isModal = false,
    zoomControls,
}) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            px: 1,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ToggleButtonGroup
                value={metric}
                exclusive
                onChange={onMetricChange}
                size="small"
                sx={{
                    'height': isModal ? 28 : 20, // 32 : 24
                    '& .MuiToggleButton-root': isModal
                        ? {
                              px: 1,
                              py: 0.25,
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                          }
                        : {},
                }}
            >
                <ToggleButton value="amount">Amount</ToggleButton>
                <ToggleButton value="count">Count</ToggleButton>
            </ToggleButtonGroup>

            {isModal && zoomControls && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Zoom out" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => zoomControls.handleZoomOut(undefined, true)}
                            disabled={!zoomControls.canZoomOut}
                            sx={{ p: 0.5 }}
                        >
                            <ZoomOut fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Zoom in" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => zoomControls.handleZoomIn(undefined, true)}
                            disabled={!zoomControls.canZoomIn}
                            sx={{ p: 0.5 }}
                        >
                            <ZoomIn fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>

        {!isModal && (
            <Box sx={{ textAlign: 'right', pr: 2.5 }}>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        color: 'text.primary',
                    }}
                >
                    Funding Activity
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontSize: '0.65rem',
                        lineHeight: 1.1,
                        color: 'text.secondary',
                        opacity: 0.8,
                    }}
                >
                    Explore funded deals
                </Typography>
            </Box>
        )}
    </Box>
);
